// Node/edge colour palette — FXC-8538 (Warp).
//
// Lifted verbatim from Bobbin's `panes/graph-utils.ts` (FXC-8165), which
// already existed to "avoid per-pane drift" between the Daily graph and
// the retired Spacewalk pane. Promoting it here makes it the single
// source of truth for every surface, not just the two Bobbin panes.
//
// Adding a new concept/predicate colour is a single-file edit. Unknown
// categories fall back to a neutral slate so a new node type is never
// invisible.

/** Stable colour-per-node-type. Union of every type a surface has needed. */
export const CONCEPT_PALETTE: Record<string, string> = {
  daily_log: "#22d3ee",
  daily_section: "#84cc16",
  github_pr: "#a78bfa",
  jira_issue: "#f59e0b",
  file_ref: "#94a3b8",
  meeting_capture: "#ec4899",
  task: "#ef4444",
  person: "#fb923c",
  // FXC-8108 vault concept types.
  project_note: "#38bdf8",
  thread_note: "#a3e635",
  snippet: "#fcd34d",
  meeting_note: "#f472b6",
  plan: "#c084fc",
  prompt: "#94a3b8",
  note: "#9ca3af",
  // FXC-8538 provenance / atomic-run kinds (geometry → mesh → case →
  // output scalars). Used by the lineage explorer and the provenance
  // adapter so a real run's atomic graph is legible by default.
  geometry: "#f97316",
  surface_mesh: "#38bdf8",
  volume_mesh: "#0ea5e9",
  mesh: "#38bdf8",
  case: "#a78bfa",
  operation: "#a78bfa",
  solve: "#8b5cf6",
  scalar: "#22d3ee",
  output: "#22d3ee",
  artifact: "#84cc16",
  plot: "#f472b6",
};

/** Resolve a node-type colour, falling back to neutral slate. */
export function colourFor(type: string): string {
  return CONCEPT_PALETTE[type] ?? "#64748b";
}

/** Stable colour-per-edge-predicate (with alpha — these draw as lines). */
export const EDGE_PALETTE: Record<string, string> = {
  child_of: "rgba(148, 163, 184, 0.45)",
  mentions: "rgba(167, 139, 250, 0.6)",
  spawns: "rgba(239, 68, 68, 0.55)",
  touched: "rgba(148, 163, 184, 0.55)",
  references: "rgba(34, 211, 238, 0.55)",
  // FXC-8538 provenance relationships.
  depends_on: "rgba(56, 189, 248, 0.5)",
  derived_from: "rgba(34, 211, 238, 0.5)",
  feeds: "rgba(148, 163, 184, 0.5)",
  produces: "rgba(132, 204, 22, 0.5)",
};

// FXC-8538: categorical palette for cluster / clique / community ids.
// A 12-hue ramp (tuned for a dark canvas); wraps for higher indices.
export const CLUSTER_PALETTE: string[] = [
  "#22d3ee", "#a78bfa", "#f59e0b", "#84cc16", "#ec4899", "#38bdf8",
  "#fb923c", "#c084fc", "#34d399", "#f472b6", "#facc15", "#60a5fa",
];

/** Stable colour for a 0-based cluster index (wraps round the ramp). */
export function clusterColourFor(index: number): string {
  return CLUSTER_PALETTE[((index % CLUSTER_PALETTE.length) + CLUSTER_PALETTE.length) % CLUSTER_PALETTE.length]!;
}

/** Resolve an edge-predicate colour, falling back to neutral slate. */
export function edgeColourFor(predicate: string): string {
  return EDGE_PALETTE[predicate] ?? "rgba(148, 163, 184, 0.4)";
}

// FXC-8108: non-Daily concept_types that the spool graph query pulls in
// when included in its `concept_types` field. A surface offers this as
// an opt-in toggle (Bobbin's "include vault").
export const SPOOL_CONCEPT_TYPES: string[] = [
  "project_note",
  "thread_note",
  "snippet",
  "flexcompute_note",
  "meeting_note",
  "personal_note",
  "flexshell_note",
  "plan",
  "meeting_prep",
  "prompt",
  "note",
  "brand_note",
  "person_note",
  "roadmap_note",
  "personal_project_note",
  "daily_note_legacy",
  "vault_root_note",
];

// FXC-8342: full vault corpus types — extends SPOOL_CONCEPT_TYPES with
// vault hierarchy (vault_note/attachment/folder) and work items
// (jira_issue/github_pr). Previously only the retired Spacewalk tab; now
// the spool graph's full-vault mode.
export const SPACEWALK_CONCEPT_TYPES: string[] = [
  ...SPOOL_CONCEPT_TYPES,
  "vault_note",
  "attachment",
  "folder",
  "jira_issue",
  "github_pr",
];
